import React, { useState } from 'react';
import { Calendar, Clock, MapPin, ChevronLeft, Users, Share2, Loader2 } from 'lucide-react';
import { formatDate, formatTime } from '../../utils/dateTime';
import type { Practice } from '../../types';
import PracticeAttendance from './PracticeAttendance';
import { useTeamStore } from '../../store/teamStore';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface PracticeHeaderProps {
  practice: Practice | null;
  isMobileView: boolean;
  timeFormat: '12h' | '24h';
  onBackToPractices: () => void;
}

export function PracticeHeader({
  practice,
  isMobileView,
  timeFormat,
  onBackToPractices
}: PracticeHeaderProps) {
  const { currentTeam } = useTeamStore();
  const [showAttendance, setShowAttendance] = useState(false);
  const [isCreatingLink, setIsCreatingLink] = useState(false);

  if (!practice) return null;

  const handleCopyShareLink = async () => {
    if (!practice?.id) return;
    setIsCreatingLink(true);
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Sign in to create a share link');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/share-practice?action=create&practiceId=${encodeURIComponent(practice.id)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string
          }
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Failed to create share link');

      const token = json?.token as string | undefined;
      if (!token) throw new Error('Failed to create share link');

      // window.location.origin is 'https://localhost' inside a Capacitor WebView,
      // so always use the canonical app URL.
      const appOrigin = import.meta.env.VITE_APP_URL || 'https://hustli.app';
      const url = `${appOrigin}/share/practice/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch (e: any) {
      console.error('Error creating share link:', e);
      toast.error(e?.message || 'Failed to create share link');
    } finally {
      setIsCreatingLink(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            {isMobileView && (
              <button
                onClick={onBackToPractices}
                className="mr-2 p-1 rounded-full hover:bg-gray-100"
                aria-label="Back to practices"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2 className="text-xl font-semibold">{practice.theme}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyShareLink}
              disabled={isCreatingLink}
              className="inline-flex items-center justify-center p-2.5 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-60 min-w-[44px] min-h-[44px]"
              aria-label="Copy share link to clipboard"
              title="Copy share link"
            >
              {isCreatingLink ? <Loader2 size={20} className="animate-spin" aria-hidden /> : <Share2 size={20} aria-hidden />}
            </button>
            <button
              onClick={() => setShowAttendance(!showAttendance)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Users size={16} />
              Attendance
            </button>
          </div>
        </div>
        
        <div className="flex flex-col gap-2 text-sm text-gray-600">
          <div className="flex items-center">
            <Calendar size={16} className="mr-2" />
            {formatDate(practice.date)}
          </div>
          <div className="flex items-center">
            <Clock size={16} className="mr-2" />
            {formatTime(practice.start_time, timeFormat)} - {formatTime(practice.end_time, timeFormat)}
          </div>
          <div className="flex items-center">
            <MapPin size={16} className="mr-2" />
            {practice.location}
          </div>
        </div>
      </div>

      {showAttendance && currentTeam && (
        <div className="mb-6">
          <PracticeAttendance
            practice={practice}
            teamId={currentTeam.id}
            onClose={() => setShowAttendance(false)}
          />
        </div>
      )}
    </>
  );
}

export default PracticeHeader; 